<template>
  <!-- eslint-disable vue/no-lone-template -->
  <page-wrapper ref="pageEl" title="MNC Balance Report">
    <div ref="page-container" class="page-container row">
      <div class="report-filter col-12">
        <div class="form">
          <!-- NOTE: Checkbox Wallet Section -->
          <div class="row ms-2">
            <el-checkbox
              v-model="checkAll"
              :indeterminate="isIndeterminate"
              :disabled="isCheckboxDisabled"
              @change="handleCheckAllChange"
            >
              All
            </el-checkbox>
            <div style="margin-right: 40px" />
            <el-checkbox-group
              v-model="walletTypes"
              :disabled="isCheckboxDisabled"
              @change="handleWalletTypeChange"
            >
              <el-checkbox
                v-for="type in walletTypeOptions"
                :key="type"
                :label="type"
                style="margin-right: 24px"
              >
                {{ type }}
              </el-checkbox>
            </el-checkbox-group>
          </div>

          <!-- NOTE: Dropdown Section -->
          <div class="section">
            <div>
              <div class="label">Wallet Name</div>
              <el-select
                v-model="selectedWalletName"
                :remote="true"
                :filter-method="onSearchWalletName"
                :disabled="isDropdownDisabled"
                style="width: 100%"
                filterable
                clearable
              >
                <el-option
                  v-for="(wallet, index) in filterWalletNamesByType"
                  :key="index"
                  :value="wallet.name"
                >
                  <template>
                    {{ wallet.name }}
                    <span :style="{ color: '#00000044' }">
                      ({{ wallet.type }})
                    </span>
                  </template>
                </el-option>
              </el-select>
            </div>
            <div>
              <div class="label">Role</div>
              <el-select
                v-model="selectedRole"
                :disabled="isDropdownDisabled || isRoleDisabled"
                style="width: 100%"
                clearable
              >
                <el-option
                  v-for="role in filterRoles"
                  :key="role.value"
                  :label="role.label"
                  :value="role.value"
                ></el-option>
              </el-select>
            </div>
            <div>
              <div class="label">MNC Type</div>
              <el-select
                v-model="selectedMorningCoin"
                :disabled="isDropdownDisabled"
                style="width: 100%"
                clearable
              >
                <el-option
                  v-for="mnc in filterMorningCoins"
                  :key="mnc.coin_id"
                  :label="mnc.coin_name"
                  :value="mnc.coin_id"
                ></el-option>
              </el-select>
            </div>
          </div>

          <!-- NOTE: Text Field Section -->
          <div class="section">
            <div>
              <div class="label">Name</div>
              <el-input v-model="name"></el-input>
            </div>
            <div>
              <div class="label">Mobile</div>
              <el-input v-model="mobileNumber"></el-input>
            </div>
            <div>
              <div class="label">Email</div>
              <el-input v-model="email" type="email"></el-input>
            </div>
          </div>
          <div class="section">
            <div>
              <div class="label">Employee ID</div>
              <el-input
                v-model="employeeId"
                :disabled="isEmployeeIdDisabled"
                type="text"
                maxlength="10"
              ></el-input>
            </div>
            <div>
              <div class="label">Created Date</div>
              <date-picker
                v-model="selectedCreatedDateRange"
                type="daterange"
              ></date-picker>
            </div>
          </div>

          <!-- NOTE: Button Section -->
          <div class="mt-2" style="display: flex; justify-content: center">
            <el-button
              :disabled="isSearchDisabled"
              type="primary"
              @click="handleSearch"
            >
              ค้นหา
            </el-button>
            <el-button
              :disabled="isClickDownloadAsCSVDisabled"
              type="info"
              @click.prevent="downloadReportFile"
            >
              ดาวน์โหลด
            </el-button>
            <el-button
              :disabled="isClearDisabled"
              plain
              type="danger"
              @click="handleClickClear"
            >
              รีเซ็ต
            </el-button>
          </div>
        </div>
      </div>

      <!-- NOTE: Report Section -->
      <div class="report-content col-12 mt-3">
        <div>
          <!-- NOTE: Header -->
          <div v-show="latestUpdateMessage != null" class="report-header">
            <div>
              <b>รายงานอัปเดต</b>
              <p>{{ latestUpdateMessage }}</p>
            </div>
            <div>
              <div>
                <p>{{ totalCount }}</p>
              </div>
              <div align="right">
                <el-button
                  v-for="index in [10, 20, 30]"
                  :key="index"
                  :type="limit == index ? 'primary' : ''"
                  circle
                  size="small"
                  @click="limit = index"
                >
                  {{ index }}
                </el-button>
              </div>
            </div>
          </div>

          <!-- NOTE: List -->
          <div class="mt-3">
            <DataTable
              :data="displayReports"
              :headers="headers"
              :loading="tableLoading"
              @rowClick="handleRowClick"
            >
            </DataTable>
          </div>

          <!-- NOTE: Pagination -->
          <div class="mt-3" style="text-align: center">
            <el-pagination
              :total="total"
              :disabled="isPaginationDisable"
              :current-page="page"
              :page-size="limit"
              background
              layout="prev, pager, next"
              @current-change="handleCurrentChange"
            >
            </el-pagination>
          </div>
        </div>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
// Dependencies
import { DateTime } from "luxon"
import { backendAdminClient } from "@/dependencies"
import { formatOrFallbackToInput } from "@/lib/dateTime"

// Components
import PageWrapper from "@/lib/components/PageWrapper"
import DatePicker from "@/lib/components/DatePicker"
import DataTable from "@/lib/components/DataTable"

export default {
  components: {
    PageWrapper,
    DatePicker,
    DataTable,
  },

  data() {
    const filterRoles = [
      { label: "Owner", value: "OWNER" },
      { label: "Resident", value: "RESIDENT" },
      { label: "Renter", value: "RENTER" },
      { label: "Panel Installer", value: "PANEL_INSTALLER" },
      { label: "Panel", value: "PANEL" },
      { label: "Booking", value: "BOOKING" },
      { label: "Contract", value: "CONTRACT" },
    ]

    const filterInput = {
      filterWalletNames: [],
      filterWalletNamesByType: [],
      filterRoles,
      filterMorningCoins: [],
    }

    const selectInputFilter = {
      selectedWalletName: undefined,
      selectedRole: undefined,
      selectedMorningCoin: undefined,
      name: undefined,
      mobileNumber: undefined,
      email: undefined,
      employeeId: undefined,
      selectedCreatedDateRange: undefined,
    }

    const isDisabledInput = {
      isCheckboxDisabled: false,
      isDropdownDisabled: false,
      isRoleDisabled: false,
      isEmployeeIdDisabled: false,
      isSearchDisabled: false,
      isClickDownloadAsCSVDisabled: false,
      isClearDisabled: false,
      isPaginationDisable: false,
    }

    const pagination = {
      page: 1,
      limit: 10,
      total: 0,
      latest_update_time: "",
    }

    const currentYear = DateTime.now().year
    const headers = [
      {
        label: "User ID",
        value: "user_id",
      },
      {
        label: "Wallet Name",
        value: "wallet_name",
      },
      {
        label: "Created Date",
        value: "created_at",
      },
      {
        label: "Status",
        value: "status",
      },
      {
        label: `Balance ${currentYear - 1}`,
        value: "last_year_balance",
      },
      {
        label: `Balance ${currentYear}`,
        value: "current_year_balance",
      },
      {
        label: "Total Balance",
        value: "total_balance",
      },
      {
        label: "Name",
        value: "full_name",
      },
      {
        label: "Mobile No.",
        value: "mobile_number",
      },
      {
        label: "Email",
        value: "email",
      },
      {
        label: "Role",
        value: "role",
      },
      {
        label: "Address No.",
        value: "address_number",
      },
      {
        label: "Project Code",
        value: "project_code",
      },
      {
        label: "Employee ID",
        value: "employee_id",
      },
    ]

    const walletTypeOptions = ["CUSTOMER", "EMPLOYEE", "ORGANIZATION"]
    const walletTypes = ["CUSTOMER", "EMPLOYEE", "ORGANIZATION"]

    return {
      checkAll: false,
      walletTypeOptions,
      walletTypes,
      isIndeterminate: true,
      ...filterInput,
      ...selectInputFilter,
      ...isDisabledInput,
      ...pagination,
      tableLoading: false,
      displayReports: [],
      reports: [],
      headers,
    }
  },
  computed: {
    totalCount() {
      if (!this.total || this.total === 0) {
        return "ไม่พบข้อมูล"
      }
      const { limit } = this
      const total = this.total.toLocaleString()
      const currentTotalItem = this.reports.length

      const isLastPage = limit > currentTotalItem
      const currentPage = +this.page || 1
      const end = (isLastPage ? total : currentPage * limit).toLocaleString()
      const start = (
        (isLastPage ? limit * currentPage - limit : end - limit) + 1
      ).toLocaleString()
      return `แสดงผล ${start} - ${end} จากทั้งหมด ${total}`
    },
    queryParams() {
      const {
        page,
        limit,
        walletTypes,
        selectedWalletName,
        selectedRole,
        selectedMorningCoin,
        name,
        mobileNumber,
        email,
        employeeId,
        selectedCreatedDateRange,
      } = this.$route.query

      return {
        page: (page && +page) || 1,
        limit: +limit || this.limit,
        wallet_types: walletTypes || this.walletTypes,
        wallet_name: selectedWalletName,
        role: selectedRole,
        coin_id: selectedMorningCoin,
        full_name: name,
        mobile_number: mobileNumber,
        email,
        employee_id: employeeId,
        created_at_range: selectedCreatedDateRange,
      }
    },
    latestUpdateMessage() {
      const format = "dd MMMM yyyy เวลา HH:mm น."
      return this.formatDateToTH(this.latest_update_time, format)
    },
  },
  watch: {
    "$route.query": async function routeQueryWatcher() {
      await this.queryBuilder()
      await this.fetchReports()

      this.$refs.pageEl.$el.querySelector(".page-content").scrollTop = 0
    },
    async limit(from, to) {
      if (from !== to) {
        await this.handleSearch()
      }
    },
    selectedWalletName(val) {
      if (!val) {
        this.isRoleDisabled = false
        return
      }

      this.isRoleDisabled = val.startsWith("ORG")
    },
    walletTypes(val) {
      this.isEmployeeIdDisabled = !val.includes("EMPLOYEE")
    },
    isRoleDisabled(val) {
      if (val) this.selectedRole = undefined
    },
    isEmployeeIdDisabled(val) {
      if (val) this.employeeId = undefined
    },
  },
  created() {
    this.setupData()
  },
  methods: {
    onSearchWalletName(val) {
      // Validate Search Length
      if (val && val.length < 2) return
      this.fetchWalletNames(val)
    },
    setupData() {
      this.queryBuilder()
      this.fetchWalletNames()
      this.fetchCoins()
      this.fetchReports()
    },
    queryBuilder() {
      const {
        page,
        limit,
        walletTypes,
        selectedWalletName,
        selectedRole,
        selectedMorningCoin,
        name,
        mobileNumber,
        email,
        employeeId,
        selectedCreatedDateRange,
      } = this.$route.query

      this.page = (page && +page) || 1
      this.limit = +limit || this.limit
      this.walletTypes = walletTypes || this.walletTypes
      this.selectedWalletName = selectedWalletName || undefined
      this.selectedRole = selectedRole || undefined
      this.selectedMorningCoin = selectedMorningCoin || undefined
      this.name = name || undefined
      this.mobileNumber = mobileNumber || undefined
      this.email = email || undefined
      this.employeeId = employeeId || undefined
      this.selectedCreatedDateRange = selectedCreatedDateRange
        ? this.setupQueryDateRange(selectedCreatedDateRange)
        : undefined
    },
    handleSearch() {
      // NOTE: if walletTypes is empty, return empty array
      if (this.walletTypes.length === 0) {
        this.displayReports = []

        return
      }

      const {
        limit,
        walletTypes,
        selectedWalletName,
        selectedRole,
        selectedMorningCoin,
        name,
        mobileNumber,
        email,
        employeeId,
        selectedCreatedDateRange,
      } = this
      let query = {
        page: 1,
        limit,
        walletTypes,
        selectedWalletName,
        selectedRole,
        selectedMorningCoin,
        name,
        mobileNumber,
        email,
        employeeId,
        selectedCreatedDateRange: this.convertDateRange(
          selectedCreatedDateRange,
        ),
      }

      // NOTE: filter out null value
      query = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== null),
      )

      // NOTE: replace route with query string
      const route = {
        name: this.$route.name,
        query,
      }
      this.$router.replace(route)
    },
    setupQueryDateRange(dateRange) {
      if (dateRange && dateRange.length > 1) {
        const [from, to] = dateRange
        return [DateTime.fromISO(from), DateTime.fromISO(to)]
      }
      return []
    },
    convertDateRange(dateRange) {
      if (dateRange && dateRange.length > 1) {
        return dateRange.map((date) => DateTime.fromISO(date).toISO())
      }
      return null
    },
    handleClickClear() {
      this.clearAllData()

      const route = {
        name: this.$route.name,
      }
      this.$router.replace(route)
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page: (page && +page) || 1 },
      }
      this.$router.replace(route)
    },
    async fetchWalletNames(search) {
      this.tableLoading = true

      // Initial Params
      const params = {}

      // Set Search
      if (search) params.name = search

      await backendAdminClient.morningCoin
        .getWalletNames(params)
        .then((response) => {
          this.filterWalletNames = response.data.result
          this.filterWalletNamesByType = this.filterWalletNames
        })
        .catch((error) => {
          const message = error.response
            ? error.response.data.message
            : error.message
          this.$message.error(
            `ไม่สามารถโหลดรายการ Wallet Name ได้เนื่องจาก "${message || "ไม่พบข้อมูล"}"`,
          )
          // eslint-disable-next-line no-console
          console.error(error)
        })
        .finally(() => {
          this.isAbleButton()
          this.tableLoading = false
        })
    },
    async fetchCoins() {
      this.tableLoading = true

      await backendAdminClient.morningCoin
        .getCoins()
        .then((response) => {
          this.filterMorningCoins = response.data.result
        })
        .catch((error) => {
          const message = error.response
            ? error.response.data.message
            : error.message
          this.$message.error(
            `ไม่สามารถโหลดรายการ Morning Coin ได้เนื่องจาก "${message || "ไม่พบข้อมูล"}"`,
          )
          // eslint-disable-next-line no-console
          console.error(error)
        })
        .finally(() => {
          this.isAbleButton()
          this.tableLoading = false
        })
    },
    async fetchReports() {
      this.tableLoading = true
      const query = this.queryParams
      await backendAdminClient.morningCoin
        .getReportBalances(query)
        .then((response) => {
          const { result } = response.data

          this.reports = result.reports
          this.displayReports = result.reports
          this.total = result.total
          this.latest_update_time = result.timestamp ? result.timestamp : null
        })
        .catch((error) => {
          const message = error.response
            ? error.response.data.message
            : error.message
          this.$message.error(
            `ไม่สามารถดู Report ได้เนื่องจาก "${message || "ไม่พบข้อมูล"}"`,
          )
          // eslint-disable-next-line no-console
          console.error(error)
        })
        .finally(() => {
          this.isAbleButton()
          this.tableLoading = false
        })
    },
    async downloadReportFile(event) {
      // get date format 2024-10-03-10:42:33
      const dateFilename = DateTime.utc()
        .setZone("Asia/Bangkok")
        .toFormat("yyyy-MM-dd-HHmmss")

      // Init file name for download report
      const fileName = `${dateFilename}-mnc-balance-reports.xlsx`
      event.preventDefault()
      this.isDisableButton()

      // Send request
      const response =
        await backendAdminClient.morningCoin.downloadReportBalances()

      // check response before download
      if (response.data) {
        const blob = new Blob([response.data])
        const link = document.createElement("a")
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName
        link.click()
        link.remove()
      }

      this.isAbleButton()
    },
    isDisableButton() {
      this.isCheckboxDisabled = true
      this.isDropdownDisabled = true
      this.isSearchDisabled = true
      this.isClearDisabled = true
      this.isClickDownloadAsCSVDisabled = true
      this.isPaginationDisable = true
    },
    isAbleButton() {
      this.isCheckboxDisabled = false
      this.isDropdownDisabled = false
      this.isSearchDisabled = false
      this.isClearDisabled = false
      this.isClickDownloadAsCSVDisabled = false
      this.isPaginationDisable = false
    },
    formatDateToTH(date, format) {
      return formatOrFallbackToInput(date, format)
    },
    handleCheckAllChange(value) {
      this.walletTypes = value ? this.walletTypeOptions : []
      this.isIndeterminate = false
      this.selectedWalletName = value ? this.selectedWalletName : undefined

      this.updateFilteredWalletNames()
    },
    handleWalletTypeChange(value) {
      const checkedCount = value.length
      this.checkAll = checkedCount === this.walletTypeOptions.length
      this.isIndeterminate =
        checkedCount > 0 && checkedCount < this.walletTypeOptions.length
      this.selectedWalletName = undefined

      this.updateFilteredWalletNames()
    },
    updateFilteredWalletNames() {
      this.filterWalletNamesByType = this.filterWalletNames.filter((wallet) => {
        if (this.checkAll) return true

        return this.walletTypes.includes(wallet.type)
      })
    },
    clearAllData() {
      this.checkAll = true
      this.walletTypes = this.walletTypeOptions
      this.selectedWalletName = undefined
      this.selectedRole = undefined
      this.selectedMorningCoin = undefined
      this.name = undefined
      this.mobileNumber = undefined
      this.email = undefined
      this.employeeId = undefined
      this.selectedCreatedDateRange = undefined
      this.isRoleDisabled = false
    },
    handleRowClick(data) {
      this.$router.push({
        name: "TransactionHistoryReportPage",
        params: {
          data,
          userId: data.user_id,
          filterMorningCoins: this.filterMorningCoins,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";
.header {
  z-index: 1;
}

.page-container {
  padding: 15px;
}

.report-cell {
  a {
    text-decoration: underline;
  }

  padding: 5px;
  border-left: 1px solid rgb(206, 204, 204);
  border-bottom: 1px solid rgb(228, 225, 225);

  &:empty::before {
    content: "N/A";
    color: #d8d8d8;
  }
}

.page-container {
  padding: 1rem;
  margin: 0;
}

.report-filter {
  width: 100%;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.report-filter .form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form .section {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, 1fr);
}

.label {
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.report-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.report-content .report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.el-button.el-button--danger.is-plain:hover,
.el-button.el-button--danger.is-plain:focus {
  color: white !important;
}

@media screen and (max-width: 1200px) {
  .form .section {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 768px) {
  .form .section {
    grid-template-columns: 1fr;
  }

  .report-filter {
    margin-bottom: 1rem;
  }
}

.el-input__inner {
  text-align: center;
}

.el-date-editor--daterange,
.el-input__inner {
  width: 100%;
}
</style>
